import type { Bundle } from "@medplum/fhirtypes";
import {
  fetchSandboxPatientFirstName,
  SANDBOX_PERSONA_NAMES,
  SandboxPersonaName,
} from "./sandbox-patient";
import type { PaginationMetadata } from "../types/pagination-metadata";

const ICD10_SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm";
const SUSPECT_MANIFESTATION_SYSTEM =
  "https://public.metriport.com/fhir/CodeSystem/suspect-manifestation";
const LOINC_SYSTEM = "http://loinc.org";

type SuspectsResponse = { metadata: PaginationMetadata; results: Bundle[] };

type MakeSuspectBundleInput = {
  patientId: string;
  conditionId: string;
  icd10Code: string;
  icd10Display: string;
  evidenceCount: number;
  firstEvidenceDate: string;
  lastEvidenceDate: string;
  hccCode: string | undefined;
  manifestationCode: string;
  manifestationDisplay: string;
  manifestationText: string;
  observationId: string;
  loincCode: string;
  loincDisplay: string;
  observationValue: number;
  observationUnit: string;
  observationDate: string;
};

function makeSuspectBundle({
  patientId,
  conditionId,
  icd10Code,
  icd10Display,
  evidenceCount,
  firstEvidenceDate,
  lastEvidenceDate,
  hccCode,
  manifestationCode,
  manifestationDisplay,
  manifestationText,
  observationId,
  loincCode,
  loincDisplay,
  observationValue,
  observationUnit,
  observationDate,
}: MakeSuspectBundleInput): Bundle {
  return {
    resourceType: "Bundle",
    entry: [
      {
        resource: {
          resourceType: "Condition",
          id: conditionId,
          subject: { reference: `Patient/${patientId}` },
          code: {
            coding: [{ system: ICD10_SYSTEM, code: icd10Code, display: icd10Display }],
            text: icd10Display,
          },
          verificationStatus: {
            coding: [
              {
                system: "http://terminology.hl7.org/CodeSystem/condition-ver-status",
                code: "unconfirmed",
                display: "Unconfirmed",
              },
            ],
          },
          extension: [
            { url: "suspect-type", valueString: "suspect" },
            { url: "evidence-count", valueInteger: evidenceCount },
            { url: "first-evidence-date", valueDateTime: firstEvidenceDate },
            { url: "last-evidence-date", valueDateTime: lastEvidenceDate },
            ...(hccCode ? [{ url: "hcc-code", valueString: hccCode }] : []),
          ],
          evidence: [
            {
              code: [
                {
                  coding: [
                    {
                      system: SUSPECT_MANIFESTATION_SYSTEM,
                      code: manifestationCode,
                      display: manifestationDisplay,
                    },
                  ],
                  text: manifestationText,
                },
              ],
            },
          ],
        },
      },
      {
        resource: {
          resourceType: "Observation",
          id: observationId,
          status: "final",
          code: {
            coding: [{ system: LOINC_SYSTEM, code: loincCode, display: loincDisplay }],
          },
          valueQuantity: { value: observationValue, unit: observationUnit },
          effectiveDateTime: observationDate,
        },
      },
    ],
  };
}

function buildPersonaBundles(patientId: string): Record<string, Bundle[]> {
  return {
    jane: [
      makeSuspectBundle({
        patientId,
        conditionId: "jane-suspect-1",
        icd10Code: "F33.1",
        icd10Display: "Major depressive disorder, recurrent, moderate",
        evidenceCount: 2,
        firstEvidenceDate: "2020-03-15",
        lastEvidenceDate: "2024-09-10",
        hccCode: "87",
        manifestationCode: "depression_phq9_10plus",
        manifestationDisplay: "PHQ-9 Score ≥ 10",
        manifestationText: "PHQ-9 total score (LOINC 44261-6) ≥ 10 on ≥1 encounter.",
        observationId: "jane-obs-1",
        loincCode: "44261-6",
        loincDisplay: "PHQ-9 total score",
        observationValue: 14,
        observationUnit: "score",
        observationDate: "2024-09-10",
      }),
      makeSuspectBundle({
        patientId,
        conditionId: "jane-suspect-2",
        icd10Code: "F31.81",
        icd10Display: "Bipolar II disorder",
        evidenceCount: 1,
        firstEvidenceDate: "2021-12-21",
        lastEvidenceDate: "2021-12-21",
        hccCode: "84",
        manifestationCode: "bipolar_lithium_signal",
        manifestationDisplay: "Lithium Use Signal",
        manifestationText: "Lithium serum level (LOINC 14334-7) present in lab results.",
        observationId: "jane-obs-2",
        loincCode: "14334-7",
        loincDisplay: "Lithium, Blood",
        observationValue: 0.6,
        observationUnit: "mmol/L",
        observationDate: "2021-12-21",
      }),
    ],
    chris: [
      makeSuspectBundle({
        patientId,
        conditionId: "chris-suspect-1",
        icd10Code: "E11.9",
        icd10Display: "Type 2 diabetes mellitus without complications",
        evidenceCount: 3,
        firstEvidenceDate: "2019-06-01",
        lastEvidenceDate: "2024-01-20",
        hccCode: "19",
        manifestationCode: "diabetes_hba1c_6p5plus",
        manifestationDisplay: "Diabetes (HbA1c ≥ 6.5%)",
        manifestationText:
          "HbA1c (LOINC 4548-4) ≥ 6.5% on ≥1 encounter. Excludes patients already coded with diabetes (ICD-10 E08–E13).",
        observationId: "chris-obs-1",
        loincCode: "4548-4",
        loincDisplay: "Hemoglobin A1c/Hemoglobin.total in Blood",
        observationValue: 7.1,
        observationUnit: "%",
        observationDate: "2024-01-20",
      }),
      makeSuspectBundle({
        patientId,
        conditionId: "chris-suspect-2",
        icd10Code: "E78.5",
        icd10Display: "Hyperlipidemia, unspecified",
        evidenceCount: 2,
        firstEvidenceDate: "2022-03-10",
        lastEvidenceDate: "2024-01-20",
        hccCode: undefined,
        manifestationCode: "hyperlipidemia_ldl_130plus",
        manifestationDisplay: "Hyperlipidemia (LDL-C ≥ 130)",
        manifestationText:
          "LDL-C (LOINC 2089-1) ≥ 130 mg/dL. Excludes patients with existing hyperlipidemia (ICD-10 E78.*).",
        observationId: "chris-obs-2",
        loincCode: "2089-1",
        loincDisplay: "Low density lipoprotein cholesterol [Mass/volume] in Serum or Plasma",
        observationValue: 162,
        observationUnit: "mg/dL",
        observationDate: "2024-01-20",
      }),
    ],
    ollie: [
      makeSuspectBundle({
        patientId,
        conditionId: "ollie-suspect-1",
        icd10Code: "F03.9",
        icd10Display: "Dementia, unspecified",
        evidenceCount: 2,
        firstEvidenceDate: "2018-11-05",
        lastEvidenceDate: "2024-04-12",
        hccCode: "52",
        manifestationCode: "dementia_moca_18_21",
        manifestationDisplay: "Dementia (MoCA 18–21)",
        manifestationText:
          "MoCA total score (LOINC 72172-0) of 18–21 (high-specificity range for MCI/dementia). Excludes patients already diagnosed with dementia (F01–F03) or Alzheimer disease (G30).",
        observationId: "ollie-obs-1",
        loincCode: "72172-0",
        loincDisplay: "Montreal Cognitive Assessment [MoCA]",
        observationValue: 19,
        observationUnit: "score",
        observationDate: "2024-04-12",
      }),
      makeSuspectBundle({
        patientId,
        conditionId: "ollie-suspect-2",
        icd10Code: "G47.33",
        icd10Display: "Obstructive sleep apnea (adult)",
        evidenceCount: 1,
        firstEvidenceDate: "2022-09-01",
        lastEvidenceDate: "2024-04-12",
        hccCode: undefined,
        manifestationCode: "osa_sleep_study",
        manifestationDisplay: "Obstructive Sleep Apnea (Sleep Study Evidence)",
        manifestationText:
          "Polysomnography or home sleep study evidence of OSA. Excludes patients already coded with OSA (ICD-10 G47.33).",
        observationId: "ollie-obs-2",
        loincCode: "72169-6",
        loincDisplay: "Epworth sleepiness scale",
        observationValue: 14,
        observationUnit: "score",
        observationDate: "2024-04-12",
      }),
    ],
    andreas: [
      makeSuspectBundle({
        patientId,
        conditionId: "andreas-suspect-1",
        icd10Code: "H36.0",
        icd10Display: "Diabetic retinopathy",
        evidenceCount: 3,
        firstEvidenceDate: "2015-07-22",
        lastEvidenceDate: "2024-02-14",
        hccCode: undefined,
        manifestationCode: "diabetic_retinopathy",
        manifestationDisplay: "Diabetic Retinopathy (Eye Exam/Imaging Signal)",
        manifestationText:
          "Flags during comprehensive eye exam or retinal imaging when documentation supports diabetic retinopathy. Excludes patients already coded with diabetic retinopathy or diabetes with ophthalmic complications (H36.0x, E08.3x, E11.3x).",
        observationId: "andreas-obs-1",
        loincCode: "4548-4",
        loincDisplay: "Hemoglobin A1c/Hemoglobin.total in Blood",
        observationValue: 9.8,
        observationUnit: "%",
        observationDate: "2024-02-14",
      }),
      makeSuspectBundle({
        patientId,
        conditionId: "andreas-suspect-2",
        icd10Code: "I25.10",
        icd10Display:
          "Atherosclerotic heart disease of native coronary artery without angina pectoris",
        evidenceCount: 2,
        firstEvidenceDate: "2017-03-30",
        lastEvidenceDate: "2024-02-14",
        hccCode: "87",
        manifestationCode: "cad_troponin_i_high",
        manifestationDisplay: "CAD (Troponin I ≥ 40 ng/L)",
        manifestationText:
          "Troponin I (LOINC 10839-9) ≥ 40 ng/L. Excludes patients already coded with CAD (ICD-10 I25.*).",
        observationId: "andreas-obs-2",
        loincCode: "10839-9",
        loincDisplay: "Troponin I.cardiac [Mass/volume] in Serum or Plasma",
        observationValue: 52,
        observationUnit: "ng/L",
        observationDate: "2024-02-14",
      }),
    ],
    kyla: [
      makeSuspectBundle({
        patientId,
        conditionId: "kyla-suspect-1",
        icd10Code: "I50.9",
        icd10Display: "Heart failure, unspecified",
        evidenceCount: 2,
        firstEvidenceDate: "2020-01-10",
        lastEvidenceDate: "2024-11-03",
        hccCode: "85",
        manifestationCode: "heart_failure_bnp_elevated",
        manifestationDisplay: "Elevated BNP Signal",
        manifestationText:
          "BNP (LOINC 42637-9) > 100 pg/mL in a patient with hypertension and diabetes.",
        observationId: "kyla-obs-1",
        loincCode: "42637-9",
        loincDisplay: "Natriuretic peptide B [Mass/volume] in Serum or Plasma",
        observationValue: 210,
        observationUnit: "pg/mL",
        observationDate: "2024-11-03",
      }),
      makeSuspectBundle({
        patientId,
        conditionId: "kyla-suspect-2",
        icd10Code: "E11.9",
        icd10Display: "Type 2 diabetes mellitus without complications",
        evidenceCount: 3,
        firstEvidenceDate: "2016-05-18",
        lastEvidenceDate: "2024-11-03",
        hccCode: "19",
        manifestationCode: "diabetes_hba1c_above_7",
        manifestationDisplay: "HbA1c > 7%",
        manifestationText: "HbA1c (LOINC 4548-4) > 7% on ≥2 encounters.",
        observationId: "kyla-obs-2",
        loincCode: "4548-4",
        loincDisplay: "Hemoglobin A1c/Hemoglobin.total in Blood",
        observationValue: 8.1,
        observationUnit: "%",
        observationDate: "2024-11-03",
      }),
    ],
  };
}

export async function getSandboxSuspects(patientId: string): Promise<SuspectsResponse> {
  const firstName = await fetchSandboxPatientFirstName(patientId);
  if (firstName && !SANDBOX_PERSONA_NAMES.includes(firstName.toLowerCase() as SandboxPersonaName)) {
    console.warn(`[suspects-mock] unknown sandbox persona firstName="${firstName}", fallback=Jane`);
  }
  const personaBundles = buildPersonaBundles(patientId);
  const personaKey =
    firstName !== null && firstName !== undefined ? firstName.toLowerCase() : undefined;
  const bundles: Bundle[] =
    (personaKey !== null && personaKey !== undefined ? personaBundles[personaKey] : undefined) ??
    [];
  return {
    metadata: { total: bundles.length, limit: 100, offset: 0 },
    results: bundles,
  };
}
